import { useEffect, useState } from 'react'
import { LockKeyhole, ShieldAlert } from 'lucide-react'
import { useLocation } from 'react-router-dom'

import { getProductionEditingAccess, type ProductionEditingAccess } from '../lib/api/productionEditing'
import { Card, CardContent } from './ui/card'

export const PRODUCTION_EDIT_PROTECTED_ROUTES = [
  '/ai-feedback',
  '/system-prompts',
  '/content-promotion',
  '/popular-hikes',
  '/fitness-hikes',
  '/exercises',
  '/exercise-videos',
  '/exercise-tracker-instructions',
  '/badges',
  '/challenges',
  '/plan-algorithm',
  '/hsi-config',
  '/system/api-keys',
  '/feature-flags'
] as const

export const isProductionEditProtectedRoute = (pathname: string) =>
  PRODUCTION_EDIT_PROTECTED_ROUTES.some((route) =>
    pathname === route || pathname.startsWith(`${route}/`)
  )

export const ProductionEditingGuardBanner = () => {
  const { pathname } = useLocation()
  const isProtectedRoute = isProductionEditProtectedRoute(pathname)

  if (!isProtectedRoute) {
    return null
  }

  return <ProductionEditingAccessBanner />
}

const ProductionEditingAccessBanner = () => {
  const [access, setAccess] = useState<ProductionEditingAccess | null>(null)

  useEffect(() => {
    let active = true
    void getProductionEditingAccess()
      .then((nextAccess) => {
        if (active) setAccess(nextAccess)
      })
      .catch(() => {
        if (active) setAccess(null)
      })

    return () => {
      active = false
    }
  }, [])

  if (!access?.isProduction) {
    return null
  }

  return (
    <Card className={access.canEdit ? 'mb-4 border-amber-500/40 bg-amber-500/5' : 'mb-4 border-red-500/40 bg-red-500/5'}>
      <CardContent className="flex items-start gap-3 py-4">
        {access.canEdit ? (
          <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
        ) : (
          <LockKeyhole className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        )}
        <div>
          <div className="font-semibold text-foreground">
            {access.canEdit ? 'Owner production editing override active' : 'Production editing locked'}
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            {access.canEdit
              ? 'This account may change protected production content. Review the environment before saving.'
              : 'This page is read-only for your account in production. Make changes in staging or contact the owner.'}
          </p>
        </div>
      </CardContent>
    </Card>
  )
}
